export type MaxCapPolicy = 'drop' | 'drop_oldest';

export interface JobOptions<T = any> {
  item?: T;
  priority?: number;
  cancelListener?: (job: Job<T>) => void;
}

export type Job<T = any> = JobOptions<T>;

interface FactoryJob<T = any> extends Job<T> {
  readonly runnable: () => void | Promise<any>;
  readonly priority: number;
  endDate: number;
}

export interface JobFactoryParams {
  rateLimit?: number;
  capacity: number;
  concurrency?: number; // ✅ NEW: Determines how many jobs can run at once
  maxCapPolicy: MaxCapPolicy;
}

export interface JobFactory<T, ReturnType> {
  schedule(runnable: () => void | Promise<any>, options?: JobOptions<T>): ReturnType;
}

export class Factory<T = any> implements JobFactory<T, Job<T> | undefined> {
  private readonly name: string;
  private readonly factoryParams: JobFactoryParams;
  private readonly queue: FactoryJob<T>[] = [];

  // ✅ NEW: Replaced single currentJob with a Set to track multiple running jobs
  private readonly currentJobs: Set<FactoryJob<T>> = new Set();

  private lastJob?: FactoryJob<T>;
  private currentTimerId?: ReturnType<typeof setTimeout>;

  // ✅ UPDATED: Handler now receives an array of all currently running jobs
  private customMaxCapHandler?: (queue: Job<T>[], runningJobs: Job<T>[]) => Job<T> | void;

  public constructor(name: string, params: JobFactoryParams) {
    if ((params.rateLimit ?? 0) < 0) {
      throw new Error('Rate limit cannot be less than 0');
    }
    if ((params.concurrency ?? 1) < 1) {
      throw new Error('Concurrency must be at least 1');
    }
    if (!params.maxCapPolicy) {
      throw new Error('You need to provide at least one MaxCapacityPolicy');
    }
    this.name = name;
    this.factoryParams = { rateLimit: 0, concurrency: 1, ...params };
  }

  public setMaxCapacityHandler(cb?: (queue: Job<T>[], runningJobs: Job<T>[]) => Job<T> | void) {
    this.customMaxCapHandler = cb;
  }

  public removeJob(job?: Job<T>) {
    if (!job) return;
    const index = this.queue.indexOf(job as FactoryJob<T>);
    if (index !== -1) {
      this.queue.splice(index, 1);
    }
  }

  // ✅ NEW: Returns an array of all active jobs
  public getCurrentJobs(): Job<T>[] {
    return Array.from(this.currentJobs);
  }

  public iterateJobs(cb: (job: Job<T>) => boolean): Job<T> | undefined {
    // Check running jobs first
    for (const job of this.currentJobs) {
      if (cb(job)) return job;
    }
    // Then check the pending queue
    return this.queue.find(cb);
  }

  private startTimer() {
    const { rateLimit = 0 } = this.factoryParams;
    const timeSinceLastJob = Date.now() - (this.lastJob?.endDate ?? 0);
    const delay = Math.max(rateLimit - timeSinceLastJob, 0);

    this.currentTimerId = setTimeout(() => {
      this.currentTimerId = undefined;
      this.checkState();
    }, delay);
  }

  private async execute(job: FactoryJob<T>) {
    this.currentJobs.add(job);

    try {
      await Promise.resolve(job.runnable());
    } catch (err) {
      console.error(`[job-manager:${this.name}] Job execution failed:`, err);
    } finally {
      job.endDate = Date.now();
      this.lastJob = job;
      this.currentJobs.delete(job); // Free up the slot
      this.checkState(); // Trigger the next job in the queue
    }
  }

  private checkState() {
    const { concurrency = 1, rateLimit = 0 } = this.factoryParams;

    // ✅ NEW: Loop to continuously pull jobs until concurrency is maxed out or queue is empty
    while (this.currentJobs.size < concurrency && this.queue.length > 0) {
      // Enforce rate limiting before starting the next job
      if (this.lastJob && rateLimit > 0) {
        const timeSinceLast = Date.now() - this.lastJob.endDate;
        if (timeSinceLast < rateLimit) {
          // If we hit the rate limit roadblock, start the timer (if not already running) and pause execution
          if (!this.currentTimerId) {
            this.startTimer();
          }
          return;
        }
      }

      // If we are under concurrency limits and not rate-limited, grab the next job
      const job = this.poll();
      if (job) {
        this.execute(job);
      } else {
        break;
      }
    }
  }

  private poll(): FactoryJob<T> | undefined {
    if (this.queue.length === 0) return undefined;

    // Find the highest priority job (O(N) search)
    let highestPriorityIdx = 0;
    for (let i = 1; i < this.queue.length; i++) {
      if (this.queue[i].priority > this.queue[highestPriorityIdx].priority) {
        highestPriorityIdx = i;
      }
    }

    // Remove and return the highest priority job
    return this.queue.splice(highestPriorityIdx, 1)[0];
  }

  private getJobsQuantity(): number {
    return this.queue.length + this.currentJobs.size;
  }

  private addJob(runnable: () => void | Promise<any>, options: JobOptions<T>): FactoryJob<T> | undefined {
    const { capacity, maxCapPolicy } = this.factoryParams;

    const job: FactoryJob<T> = {
      runnable,
      item: options.item,
      cancelListener: options.cancelListener,
      priority: options.priority ?? 0,
      endDate: 0,
    };

    if (this.getJobsQuantity() < capacity) {
      this.queue.push(job);
      return job;
    }

    if (this.customMaxCapHandler) {
      try {
        this.customMaxCapHandler(this.queue, Array.from(this.currentJobs));
        if (this.getJobsQuantity() < capacity) {
          this.queue.push(job);
          return job;
        }
        console.warn(`[job-manager:${this.name}] Capacity handler did not free enough space.`);
      } catch (e) {
        console.error(`[job-manager:${this.name}] customMaxCapHandler error:`, e);
      }
    }

    if (maxCapPolicy === 'drop_oldest' && this.queue.length > 0) {
      const removed = this.queue.shift();
      if (removed?.cancelListener) {
        removed.cancelListener(removed);
      }
      this.queue.push(job);
      return job;
    }

    return undefined; // Falls through for 'drop' policy
  }

  public schedule(runnable: () => void | Promise<any>, options: JobOptions<T> = {}): Job<T> | undefined {
    const job = this.addJob(runnable, options);

    if (job) {
      this.checkState();
      return job;
    }

    console.warn(`[job-manager:${this.name}] Refused to add the job (Capacity Reached)`);
    return undefined;
  }
}

// --- Global Factory Management ---

const activeFactories: Record<string, Factory<any>> = {};

export const initFactory = <T = any>(key: string, props: JobFactoryParams): Factory<T> => {
  return new Factory<T>(key, props);
};
